/**
 * PeerJS network adapter for dechess.
 *
 * Pins the project signaling host and retries refused sockets. dignity.js
 * 1.1.1 keeps the Peer after dial errors, but its default hosts are not
 * the dechess Vercel server.
 */

import { lobbyDebug } from './lobbyDebug.js';
import { createSignalingDialGate } from './signalingThrottle.js';

const DEFAULT_START_ATTEMPTS = 5;
const DEFAULT_START_RETRY_DELAYS_MS = Object.freeze([400, 800, 1600, 3200]);

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isFatalSignalingError(error) {
  const type = String(error?.type || '');
  const message = String(error?.message || error || '');
  return /unavailable-id|invalid-key/i.test(type)
    || /unavailable-id|invalid-key|is taken|invalid api key/i.test(message);
}

function parsePeerJsServerUrl(url) {
  const parsed = new URL(url);
  const secure = parsed.protocol === 'wss:';
  const host = parsed.hostname;
  const port = parsed.port ? Number(parsed.port) : (secure ? 443 : 80);
  const key = parsed.searchParams.get('key') || 'peerjs';
  let path = parsed.pathname || '/';
  if (path.endsWith('/peerjs')) {
    path = path.slice(0, -'/peerjs'.length) || '/';
  }
  if (path !== '/' && !path.endsWith('/')) {
    path += '/';
  }
  return { secure, host, port, path, key };
}

export function createDeChessPeerJSNetworkAdapter(options = {}) {
  return new DeChessPeerJSNetworkAdapter(options);
}

class DeChessPeerJSNetworkAdapter {
  constructor({
    urls = [],
    connectTimeoutMs = 12_000,
    iceServers = null,
    PeerImpl = null,
    startAttempts = DEFAULT_START_ATTEMPTS,
    startRetryDelaysMs = DEFAULT_START_RETRY_DELAYS_MS,
    reconnectBaseDelayMs = 500,
    reconnectMaxDelayMs = 15_000
  } = {}) {
    this.urls = urls.length ? urls : [];
    this.url = this.urls[0] || '';
    this.connectTimeoutMs = connectTimeoutMs;
    this.iceServers = iceServers;
    this.PeerImpl = PeerImpl;
    this.startAttempts = startAttempts;
    this.startRetryDelaysMs = startRetryDelaysMs;
    this.reconnectBaseDelayMs = reconnectBaseDelayMs;
    this.reconnectMaxDelayMs = reconnectMaxDelayMs;
    this.nodeId = null;
    this.peer = null;
    this.connections = new Map();
    this.pendingConnections = new Map();
    this.messageHandlers = new Set();
    this._restarting = null;
    this._stopped = false;
    this._starting = false;
    this._session = 0;
    this._reconnectAttempt = 0;
    this._reconnectTimer = null;
    this._reconnectInflight = false;
    this._dialGate = createSignalingDialGate();
  }

  async start(nodeId) {
    if (!nodeId) {
      throw new Error('PeerJS network adapter requires nodeId on start');
    }
    this._session += 1;
    const session = this._session;
    this._stopped = false;
    this._reconnectAttempt = 0;
    this._clearReconnectTimer();
    this._destroyPeer();

    let lastError;
    this._starting = true;
    try {
      for (const candidateUrl of this.urls) {
        for (let attempt = 1; attempt <= this.startAttempts; attempt += 1) {
          if (this._stopped || this._session !== session) {
            throw new Error('PeerJS network adapter stopped');
          }
          try {
            await this.startWithUrl(nodeId, candidateUrl);
            if (this._stopped || this._session !== session) {
              this._destroyPeer();
              throw new Error('PeerJS network adapter stopped');
            }
            this.url = candidateUrl;
            lobbyDebug('peerjs-started', { url: candidateUrl, nodeId, attempt });
            return;
          } catch (error) {
            lastError = error;
            lobbyDebug('peerjs-start-fail', {
              url: candidateUrl,
              attempt,
              message: error?.message || String(error)
            });
            if (isFatalSignalingError(error) || attempt >= this.startAttempts) {
              break;
            }
            const delay = this.startRetryDelaysMs[attempt - 1] ?? 4_000;
            lobbyDebug('peerjs-start-retry', { url: candidateUrl, attempt, delay });
            if (delay > 0) {
              await wait(delay);
            }
          }
        }
      }
    } finally {
      this._starting = false;
    }
    throw lastError || new Error('Unable to connect PeerJS network adapter');
  }

  async startWithUrl(nodeId, url) {
    this.nodeId = nodeId;
    const server = parsePeerJsServerUrl(url);
    const PeerCtor = this.PeerImpl || (await import('peerjs')).Peer;
    if (typeof PeerCtor !== 'function') {
      throw new Error('PeerJS Peer implementation is not available');
    }

    await new Promise((resolve, reject) => {
      const peerConfig = {
        host: server.host,
        port: server.port,
        path: server.path,
        secure: server.secure,
        key: server.key,
        debug: 0
      };
      if (Array.isArray(this.iceServers) && this.iceServers.length > 0) {
        peerConfig.config = { iceServers: this.iceServers };
      }

      const peer = new PeerCtor(nodeId, peerConfig);
      let opened = false;

      const timeout = setTimeout(() => {
        try {
          peer.destroy();
        } catch {
          // Ignore.
        }
        reject(new Error(`Unable to connect PeerJS network adapter to ${url}`));
      }, this.connectTimeoutMs);

      const onOpen = () => {
        if (opened) {
          return;
        }
        opened = true;
        clearTimeout(timeout);
        // CRITICAL: remove startup error handler so later peer-unavailable
        // errors do not destroy the live Peer (dignity.js bug).
        peer.off('error', onStartupError);
        peer.on('error', onRuntimeError);
        this.peer = peer;
        resolve();
      };

      const onStartupError = (error) => {
        if (opened) {
          return;
        }
        clearTimeout(timeout);
        try {
          peer.destroy();
        } catch {
          // Ignore.
        }
        reject(error || new Error(`Unable to connect PeerJS network adapter to ${url}`));
      };

      const onRuntimeError = (error) => {
        const type = error?.type || '';
        const message = error?.message || String(error || 'peer-error');
        lobbyDebug('peerjs-runtime-error', { type, message, nodeId: this.nodeId });
        // Keep the Peer instance. peer-unavailable is a dead dial, and a
        // network drop must stay reconnectable so existing WebRTC links survive.
      };

      peer.on('open', onOpen);
      peer.on('error', onStartupError);
      peer.on('connection', (connection) => {
        this.attachConnectionHandlers(connection);
      });
      peer.on('disconnected', () => {
        lobbyDebug('peerjs-disconnected', { nodeId: this.nodeId });
        if (!opened || this._stopped || this._starting) {
          return;
        }
        this._scheduleSignalingReconnect();
      });
      peer.on('close', () => {
        lobbyDebug('peerjs-closed', { nodeId: this.nodeId });
        if (this.peer === peer) {
          this.peer = null;
        }
      });
    });
  }

  attachConnectionHandlers(connection) {
    const remoteId = connection?.peer;
    if (!remoteId || typeof connection.on !== 'function') {
      return;
    }
    // Prefer an already-open link; do not replace it with a half-open duplicate.
    const existing = this.connections.get(remoteId);
    if (existing && existing !== connection && existing.open && !connection.open) {
      connection.on('open', () => {
        try {
          connection.close?.();
        } catch {
          // Ignore duplicate teardown failures.
        }
      });
      return;
    }
    this.connections.set(remoteId, connection);
    try {
      connection.off?.('data');
      connection.off?.('close');
      connection.off?.('error');
    } catch {
      // PeerJS EventEmitter variants differ; duplicate handlers are guarded below.
    }
    connection.on('data', (payload) => {
      const deliveries = [];
      for (const handler of this.messageHandlers) {
        deliveries.push(handler(payload));
      }
      return Promise.all(deliveries);
    });
    connection.on('close', () => {
      if (this.connections.get(remoteId) === connection) {
        this.connections.delete(remoteId);
      }
    });
    connection.on('error', (error) => {
      lobbyDebug('peerjs-connection-error', {
        peerId: remoteId,
        type: error?.type || '',
        message: error?.message || String(error || 'connection-error'),
        open: Boolean(connection.open)
      });
      // A failed send (e.g. MessageToBig) leaves the channel open; keep it.
      if (connection.open) {
        return;
      }
      if (this.connections.get(remoteId) === connection) {
        this.connections.delete(remoteId);
      }
    });
  }

  waitForConnectionOpen(connection, remotePeerId) {
    if (connection?.open) {
      return Promise.resolve(connection);
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Unable to connect to peer ${remotePeerId}`));
      }, this.connectTimeoutMs);

      const onOpen = () => {
        clearTimeout(timeout);
        resolve(connection);
      };
      const onError = (error) => {
        clearTimeout(timeout);
        reject(error || new Error(`Unable to connect to peer ${remotePeerId}`));
      };

      connection.on('open', onOpen);
      connection.on('error', onError);
    });
  }

  _clearReconnectTimer() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  _destroyPeer() {
    const peer = this.peer;
    this.peer = null;
    if (!peer) {
      return;
    }
    try {
      peer.destroy();
    } catch {
      // Ignore.
    }
  }

  _scheduleSignalingReconnect() {
    if (this._stopped || this._starting || this._reconnectTimer || this._reconnectInflight) {
      return;
    }
    const session = this._session;
    const delay = Math.min(
      this.reconnectMaxDelayMs,
      this.reconnectBaseDelayMs * (2 ** this._reconnectAttempt)
    );
    this._reconnectAttempt += 1;
    lobbyDebug('peerjs-reconnect-scheduled', { nodeId: this.nodeId, delay, attempt: this._reconnectAttempt });
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._stopped || this._session !== session) {
        return;
      }
      void this._reconnectSignaling(session);
    }, delay);
  }

  async _waitForPeerOpen(peer) {
    if (typeof peer.reconnect !== 'function') {
      throw new Error('PeerJS reconnect is not available');
    }
    await new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('PeerJS reconnect timeout'));
      }, Math.min(this.connectTimeoutMs, 8_000));
      const onOpen = () => {
        cleanup();
        resolve();
      };
      const onError = (error) => {
        if (error?.type === 'peer-unavailable') {
          return;
        }
        cleanup();
        reject(error || new Error('PeerJS reconnect failed'));
      };
      function cleanup() {
        clearTimeout(timer);
        peer.off?.('open', onOpen);
        peer.off?.('error', onError);
      }
      peer.on?.('open', onOpen);
      peer.on?.('error', onError);
      try {
        peer.reconnect();
      } catch (error) {
        cleanup();
        reject(error);
      }
    });
  }

  async _reconnectSignaling(session) {
    if (this._stopped || this._session !== session || this._reconnectInflight) {
      return;
    }
    this._reconnectInflight = true;
    try {
      const peer = this.peer;
      if (peer && !peer.destroyed && peer.disconnected) {
        try {
          await this._waitForPeerOpen(peer);
          if (this._stopped || this._session !== session) {
            return;
          }
          this._reconnectAttempt = 0;
          lobbyDebug('peerjs-reconnected', { nodeId: this.nodeId });
          return;
        } catch (error) {
          lobbyDebug('peerjs-reconnect-fail', { message: error?.message || String(error) });
        }
      }
      if (this._stopped || this._session !== session || !this.nodeId) {
        return;
      }
      this._reconnectInflight = false;
      await this.start(this.nodeId);
    } catch (error) {
      lobbyDebug('peerjs-reconnect-fail', { message: error?.message || String(error) });
    } finally {
      this._reconnectInflight = false;
    }
    if (!this._stopped && !this.peer) {
      this._scheduleSignalingReconnect();
    }
  }

  async ensurePeerReady() {
    if (this.peer && !this.peer.destroyed && !this.peer.disconnected) {
      return this.peer;
    }
    if (!this.nodeId || !this.urls.length) {
      throw new Error('PeerJS network adapter has not been started');
    }
    this._clearReconnectTimer();
    if (!this._restarting) {
      const session = this._session;
      this._restarting = this._reconnectSignaling(session).finally(() => {
        this._restarting = null;
      });
    }
    await this._restarting;
    if (!this.peer || this.peer.destroyed) {
      throw new Error('PeerJS peer is not ready');
    }
    return this.peer;
  }

  prioritizeDial(remotePeerId) {
    this._dialGate?.prioritize?.(remotePeerId);
  }

  async connectToPeer(remotePeerId) {
    if (!remotePeerId || remotePeerId === this.nodeId) {
      return null;
    }

    const existing = this.connections.get(remotePeerId);
    if (existing && existing.open) {
      return existing;
    }
    if (this.pendingConnections.has(remotePeerId)) {
      return this.pendingConnections.get(remotePeerId);
    }

    // Incoming PeerJS links are registered before `open`. Wait for that instead
    // of dialing a second channel that races the first and drops early sends.
    if (existing && !existing.open) {
      const pendingInbound = this.waitForConnectionOpen(existing, remotePeerId)
        .finally(() => {
          this.pendingConnections.delete(remotePeerId);
        });
      this.pendingConnections.set(remotePeerId, pendingInbound);
      return pendingInbound;
    }

    if (!this._dialGate.allow(remotePeerId)) {
      lobbyDebug('peerjs-dial-throttled', {
        peerId: remotePeerId,
        reason: 'cooldown-or-burst'
      });
      return existing || null;
    }

    const peer = await this.ensurePeerReady();
    if (!peer || typeof peer.connect !== 'function') {
      throw new Error('PeerJS peer is not ready');
    }

    const pending = new Promise((resolve, reject) => {
      let connection;
      try {
        // 'json' drops any message >= 16300 bytes (MessageToBig); signed game
        // snapshots pass that around ply 13. 'binary' chunks large payloads.
        // Inbound links still use whatever serialization the remote dialed —
        // both clients must reload onto this build or one side stays on JSON.
        connection = peer.connect(remotePeerId, {
          reliable: true,
          serialization: 'binary'
        });
      } catch (error) {
        reject(error);
        return;
      }

      if (!connection || typeof connection.on !== 'function') {
        reject(new Error(
          `PeerJS connect() returned no DataConnection for ${remotePeerId} `
          + `(peer destroyed=${Boolean(peer.destroyed)} disconnected=${Boolean(peer.disconnected)})`
        ));
        return;
      }

      // Register immediately so a simultaneous inbound dial waits on this link.
      this.connections.set(remotePeerId, connection);

      const timeout = setTimeout(() => {
        try {
          connection.close?.();
        } catch {
          // Ignore.
        }
        if (this.connections.get(remotePeerId) === connection) {
          this.connections.delete(remotePeerId);
        }
        reject(new Error(`Unable to connect to peer ${remotePeerId}`));
      }, this.connectTimeoutMs);

      connection.on('open', () => {
        clearTimeout(timeout);
        this.attachConnectionHandlers(connection);
        resolve(connection);
      });

      connection.on('error', (error) => {
        clearTimeout(timeout);
        if (connection.open) {
          return;
        }
        if (this.connections.get(remotePeerId) === connection) {
          this.connections.delete(remotePeerId);
        }
        reject(error || new Error(`Unable to connect to peer ${remotePeerId}`));
      });
    }).finally(() => {
      this.pendingConnections.delete(remotePeerId);
    });

    this.pendingConnections.set(remotePeerId, pending);
    return pending;
  }

  onMessage(handler) {
    this.messageHandlers.add(handler);
  }

  offMessage(handler) {
    this.messageHandlers.delete(handler);
  }

  async broadcast(message) {
    const deliveries = [];
    for (const connection of this.connections.values()) {
      if (connection.open) {
        deliveries.push(connection.send(message));
      }
    }
    await Promise.all(deliveries);
  }

  async sendToPeers(message, peerIds = []) {
    const targets = [...new Set((peerIds || []).filter(Boolean))];
    if (!targets.length) {
      return;
    }
    for (const peerId of targets) {
      try {
        await this.connectToPeer(peerId);
      } catch (error) {
        lobbyDebug('peerjs-send-connect-fail', {
          peerId,
          message: error?.message || String(error)
        });
      }
    }
    const deliveries = [];
    for (const peerId of targets) {
      const connection = this.connections.get(peerId);
      if (connection?.open) {
        deliveries.push(connection.send(message));
      }
    }
    await Promise.all(deliveries);
  }

  async disconnectPeer(remotePeerId) {
    const connection = this.connections.get(remotePeerId);
    if (connection && typeof connection.close === 'function') {
      connection.close();
    }
    this.connections.delete(remotePeerId);
  }

  getOpenConnectionCount() {
    return this.listOpenPeerIds().length;
  }

  listOpenPeerIds() {
    const ids = [];
    for (const [peerId, connection] of this.connections.entries()) {
      if (connection.open) {
        ids.push(peerId);
      }
    }
    return ids;
  }

  isConnectedTo(remotePeerId) {
    const connection = this.connections.get(remotePeerId);
    return Boolean(connection && connection.open);
  }

  async stop() {
    this._stopped = true;
    this._session += 1;
    this._clearReconnectTimer();
    for (const connection of this.connections.values()) {
      try {
        connection.close?.();
      } catch {
        // Ignore.
      }
    }
    this.connections.clear();
    this.pendingConnections.clear();
    this._destroyPeer();
  }
}
